import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  LayoutDashboard, Users, ReceiptText, ArrowLeftRight, Scale, BarChart3,
  CalendarDays, FileText, Plus, Search, Bell, ChevronDown, TrendingUp,
  TrendingDown, MoreHorizontal, X, CheckCircle2, Upload, Clock, MapPin,
  Wallet, Landmark, CreditCard, Banknote, FileCheck2, LogOut, Shield, UserCog, Trash2,
  Building2, Download, FileDown, Paperclip, ExternalLink, KeyRound
} from "lucide-react";
import "@/App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
axios.defaults.withCredentials = true;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let refreshPromise = null;
axios.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config;
  const url = cfg?.url || "";
  if (error.response?.status !== 401 || cfg._retried || url.includes("/auth/login") || url.includes("/auth/refresh")) {
    throw error;
  }
  cfg._retried = true;
  refreshPromise = refreshPromise || axios.post(`${API}/auth/refresh`).finally(() => { refreshPromise = null; });
  await refreshPromise;
  return axios(cfg);
});

function formatApiError(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
}

const NAV = [
  { id: "overview", label: "Genel Bakış", icon: LayoutDashboard },
  { id: "clients", label: "Müvekkiller", icon: Users },
  { id: "transactions", label: "Cari Hareketler", icon: ArrowLeftRight },
  { id: "invoices", label: "Faturalar", icon: ReceiptText },
  { id: "cases", label: "Dava & İcra", icon: Scale },
  { id: "reports", label: "Gelir & Gider", icon: BarChart3 },
  { id: "calendar", label: "Takvim", icon: CalendarDays },
  { id: "documents", label: "Belgeler", icon: FileText },
];

const MONEY = (n) =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);

const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY_DATA = { clients: [], transactions: [], invoices: [], cases: [], documents: [], calendar: [] };

const CLIENT_OPTIONS = (data) => (data.clients || []).map((c) => c.name);

const PAYMENT_ICONS = { "Nakit": Banknote, "Kredi kartı": CreditCard, "Havale / EFT": Landmark };

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null); // null=checking, false=guest, object=logged in
  const [showUsers, setShowUsers] = useState(false);
  const [showFirm, setShowFirm] = useState(false);

  const [showPwd, setShowPwd] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    if (location.hash) navigate("/", { replace: true });
  }, [location.hash, navigate]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* ignore */ }
    setUser(false);
    toast.success("Çıkış yapıldı");
  };

  if (user === null) return <LoadingScreen />;
  if (user === false) return <LoginPage onSuccess={setUser} />;
  return (
    <>
      <Dashboard user={user} onLogout={handleLogout} onManageUsers={() => setShowUsers(true)} onFirmSettings={() => setShowFirm(true)} onChangePassword={() => setShowPwd(true)} />
      {showUsers && <UsersModal me={user} onClose={() => setShowUsers(false)} />}
      {showFirm && <FirmSettingsModal onClose={() => setShowFirm(false)} />}
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </>
  );
}

function LoadingScreen() {
  return (
    <div className="auth-screen">
      <div className="auth-card"><div className="brand-mark">L</div><p>Oturum kontrol ediliyor...</p></div>
    </div>
  );
}

function LoginPage({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      toast.success(`Hoş geldiniz, ${r.data.name}`);
      onSuccess(r.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Giriş yapılamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={submit} data-testid="login-form">
        <div className="brand-mark large">L</div>
        <h1>LexiSync</h1>
        <span className="eyebrow gold-txt">GÖRKEM PEKER HUKUK VE DANIŞMANLIK</span>
        <p>Büro yönetim panelinize erişmek için e-posta ve şifrenizle oturum açın.</p>
        <label className="auth-field">E-posta
          <input data-testid="login-email-input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@buro.com" />
        </label>
        <label className="auth-field">Şifre
          <input data-testid="login-password-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </label>
        {error && <div className="auth-error" data-testid="login-error">{error}</div>}
        <button data-testid="login-submit-btn" type="submit" className="primary-btn auth-btn" disabled={busy}>
          <KeyRound size={15} /> {busy ? "Giriş yapılıyor..." : "Giriş yap"}
        </button>
        <div className="auth-footer">
          <Shield size={12} /> Hesabınız yoksa büro yöneticinizden kullanıcı oluşturmasını isteyin.
        </div>
      </form>
    </div>
  );
}

function Dashboard({ user, onLogout, onManageUsers, onFirmSettings, onChangePassword }) {
  const [active, setActive] = useState("overview");
  const [data, setData] = useState(EMPTY_DATA);
  const [dashboard, setDashboard] = useState({ clients: 0, cases: 0, income: 0, expense: 0, outstanding: 0 });
  const [reports, setReports] = useState({ monthly: [], categories: [], total_income: 0, total_expense: 0, net: 0 });
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null);
  const [, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [d, c, t, i, cs, doc, cal, rep] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/transactions`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/cases`),
        axios.get(`${API}/documents`),
        axios.get(`${API}/calendar`),
        axios.get(`${API}/reports/summary`),
      ]);
      setDashboard(d.data);
      setReports(rep.data);
      setData({ clients: c.data, transactions: t.data, invoices: i.data, cases: cs.data, documents: doc.data, calendar: cal.data });
    } catch (e) {
      // Backend request failed — leave existing state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const current = NAV.find((x) => x.id === active);
  const list = data[active] || [];
  const filtered = useMemo(
    () => list.filter((x) => JSON.stringify(x).toLowerCase().includes(query.toLowerCase())),
    [list, query]
  );

  const addItem = async (item, type) => {
    try {
      const res = await axios.post(`${API}/${type}`, item);
      setData((d) => ({ ...d, [type]: [res.data, ...(d[type] || [])] }));
      loadAll();
      toast.success("Kayıt başarıyla eklendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt eklenemedi");
    }
    setModal(null);
  };

  const updateItem = async (type, id, updates) => {
    try {
      const res = await axios.put(`${API}/${type}/${id}`, updates);
      setData((d) => ({ ...d, [type]: (d[type] || []).map((x) => (x.id === id ? res.data : x)) }));
      loadAll();
      toast.success("Kayıt güncellendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Güncellenemedi");
    }
    setModal(null);
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/${type}/${id}`);
      setData((d) => ({ ...d, [type]: (d[type] || []).filter((x) => x.id !== id) }));
      loadAll();
      toast.success("Kayıt silindi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
    setModal(null);
  };

  const bulkDelete = async (type, ids) => {
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} kaydı silmek istediğinizden emin misiniz?`)) return;
    try {
      const res = await axios.post(`${API}/${type}/bulk-delete`, { ids });
      setData((d) => ({ ...d, [type]: (d[type] || []).filter((x) => !ids.includes(x.id)) }));
      loadAll();
      toast.success(`${res.data.deleted} kayıt silindi`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  const resetDemo = async () => {
    if (!window.confirm("Yalnızca 'Örnek veri yükle' ile eklenmiş demo kayıtlar silinecek. Gerçek verileriniz güvende. Devam edilsin mi?")) return;
    try {
      const res = await axios.post(`${API}/admin/reset-demo`);
      const total = Object.values(res.data.cleared).reduce((a, b) => a + b, 0);
      toast.success(total > 0 ? `${total} demo kayıt temizlendi` : "Silinecek demo kayıt yok");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Temizlenemedi");
    }
  };

  const loadDemo = async () => {
    try {
      await axios.post(`${API}/seed`);
      toast.success("Örnek veriler yüklendi");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Yüklenemedi");
    }
  };

  const openEdit = (type, record) => setModal({ type, mode: "edit", record });
  const openCreate = (type) => setModal({ type, mode: "create" });

  const hasAdd = ["clients", "transactions", "invoices", "cases", "documents", "calendar"].includes(active);

  return (
    <div className="app-shell">
      <Toaster theme="dark" position="top-right" richColors />
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">L</div>
          <div>
            <strong>LexiSync</strong>
            <span>HUKUK YÖNETİMİ</span>
          </div>
        </div>
        <div className="workspace">
          <span>ÇALIŞMA ALANI</span>
          <button data-testid="workspace-selector" className="workspace-btn">
            Görkem Peker Hukuk ve Danışmanlık <ChevronDown size={14} />
          </button>
        </div>
        <nav>
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              className={active === id ? "nav-item active" : "nav-item"}
              onClick={() => { setActive(id); setQuery(""); }}
            >
              <Icon size={18} />
              <span>{label}</span>
              {id === "calendar" && (data.calendar || []).length > 0 && <b>{data.calendar.length}</b>}
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="secure">
            <CheckCircle2 size={16} />
            <span>Verileriniz güvende</span>
          </div>
          {user.role === "Yönetici" && (
            <>
              <button data-testid="firm-settings-btn" className="sidebar-action" onClick={onFirmSettings}>
                <Building2 size={14} /> <span>Büro bilgileri</span>
              </button>
              <button data-testid="manage-users-btn" className="sidebar-action" onClick={onManageUsers}>
                <UserCog size={14} /> <span>Kullanıcı yönetimi</span>
              </button>
              <button data-testid="load-demo-btn" className="sidebar-action" onClick={loadDemo} title="Örnek veri yükle (boş modüller için)">
                <Plus size={14} /> <span>Örnek veri yükle</span>
              </button>
              <button data-testid="reset-demo-btn" className="sidebar-action danger" onClick={resetDemo} title="Tüm örnek verileri sil">
                <Trash2 size={14} /> <span>Demo verilerini temizle</span>
              </button>
            </>
          )}
          <button data-testid="change-password-btn" className="sidebar-action" onClick={onChangePassword}>
            <KeyRound size={14} /> <span>Şifremi değiştir</span>
          </button>
          <div className="profile">
            {user.picture ? <img className="avatar img" src={user.picture} alt={user.name} /> : <div className="avatar">{initials(user.name)}</div>}
            <div>
              <strong data-testid="sidebar-user-name">{user.name}</strong>
              <span>{user.role}</span>
            </div>
            <button data-testid="logout-btn" className="icon-btn" onClick={onLogout} title="Çıkış yap"><LogOut size={14} /></button>
          </div>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <div className="crumb">LexiSync <span>/</span> {current.label}</div>
          <div className="top-actions">
            <button data-testid="notifications-button" className="icon-btn">
              <Bell size={18} />
              <i></i>
            </button>
            <div className="top-profile">
              {user.picture ? <img className="avatar small img" src={user.picture} alt={user.name} /> : <div className="avatar small">{initials(user.name)}</div>}
              <span>{user.name}</span>
              <ChevronDown size={14} />
            </div>
          </div>
        </header>
        <section className="content">
          <div className="page-heading">
            <div>
              <div className="eyebrow">
                {active === "overview" ? "GÜNAYDIN" : "ÇALIŞMA ALANI"}
              </div>
              <h1>
                {active === "overview" ? (
                  <>Günaydın, {user.name.split(" ")[0]}<span className="gold">.</span></>
                ) : (
                  current.label
                )}
              </h1>
              <p>
                {active === "overview"
                  ? "Büronuzun finansal ve hukuki görünümü bugün böyle."
                  : `${current.label} kayıtlarını ve iş akışınızı yönetin.`}
              </p>
            </div>
            {hasAdd && (
              <button data-testid={`add-${active}-button`} className="primary-btn" onClick={() => openCreate(active)}>
                <Plus size={17} /> Yeni Kayıt
              </button>
            )}
          </div>

          {active === "overview" && (
            <Overview dashboard={dashboard} data={data} setActive={setActive} openCreate={openCreate} />
          )}
          {["clients", "transactions", "invoices", "cases"].includes(active) && (
            <Module
              key={active}
              id={active}
              list={filtered}
              query={query}
              setQuery={setQuery}
              onEdit={(r) => openEdit(active, r)}
              onBulkDelete={(ids) => bulkDelete(active, ids)}
            />
          )}
          {active === "reports" && <Reports reports={reports} />}
          {active === "calendar" && (
            <Calendar
              events={data.calendar || []}
              cases={data.cases || []}
              onEditEvent={(e) => openEdit("calendar", e)}
              onEditCase={(c) => openEdit("cases", c)}
            />
          )}
          {active === "documents" && (
            <Documents
              docs={filtered}
              query={query}
              setQuery={setQuery}
              onEdit={(r) => openEdit("documents", r)}
              onBulkDelete={(ids) => bulkDelete("documents", ids)}
            />
          )}
        </section>
      </main>
      {modal && (
        <Modal
          type={modal.type}
          mode={modal.mode}
          record={modal.record}
          data={data}
          onClose={() => setModal(null)}
          onSave={addItem}
          onUpdate={updateItem}
          onDelete={deleteItem}
        />
      )}
    </div>
  );
}

/* ---------------- OVERVIEW ---------------- */
function Overview({ dashboard, data, setActive, openCreate }) {
  const upcoming = (data.cases || [])
    .filter((c) => c.next_date)
    .sort((a, b) => (a.next_date || "").localeCompare(b.next_date || ""))
    .slice(0, 3);

  return (
    <>
      <div className="metric-grid">
        <Metric label="Toplam Müvekkil" value={dashboard.clients} suffix=" aktif kayıt" icon={Users} tone="gold" testId="metric-clients" />
        <Metric label="Açık Dosyalar" value={dashboard.cases} suffix=" dosya" icon={Scale} tone="blue" testId="metric-cases" />
        <Metric label="Bu Ay Tahsilat" value={MONEY(dashboard.income)} suffix="" icon={TrendingUp} tone="green" testId="metric-income" />
        <Metric label="Bekleyen Alacak" value={MONEY(dashboard.outstanding)} suffix="" icon={ArrowLeftRight} tone="orange" testId="metric-outstanding" />
      </div>

      <div className="dashboard-grid">
        <section className="panel chart-panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">FİNANSAL AKIŞ</span>
              <h2>Gelir & gider görünümü</h2>
            </div>
            <button data-testid="chart-period-selector" className="select-btn">Son 6 ay <ChevronDown size={14} /></button>
          </div>
          <div className="chart-legend">
            <span><i className="dot income"></i>Gelir</span>
            <span><i className="dot expense"></i>Gider</span>
            <strong>{MONEY(dashboard.income)} <small>Bu ay</small></strong>
          </div>
          <div className="chart">
            <div className="grid-lines"><i></i><i></i><i></i><i></i></div>
            <svg viewBox="0 0 700 220" preserveAspectRatio="none">
              <path className="area" d="M0 168 C80 155 90 108 155 126 S230 92 280 116 S345 68 405 94 S470 120 520 66 S595 84 700 30 L700 220 L0 220Z" />
              <path className="line" d="M0 168 C80 155 90 108 155 126 S230 92 280 116 S345 68 405 94 S470 120 520 66 S595 84 700 30" />
              <path className="line secondary" d="M0 194 C90 182 110 164 165 174 S250 142 310 166 S390 138 430 154 S510 132 560 145 S640 120 700 130" />
            </svg>
            <div className="chart-labels">
              <span>Oca</span><span>Şub</span><span>Mar</span><span>Nis</span><span>May</span><span>Haz</span>
            </div>
          </div>
        </section>

        <section className="panel deadlines">
          <div className="panel-head">
            <div>
              <span className="eyebrow">YAKLAŞANLAR</span>
              <h2>Duruşma & hatırlatmalar</h2>
            </div>
            <button data-testid="calendar-view-button" className="text-btn" onClick={() => setActive("calendar")}>
              Tümünü gör <span>→</span>
            </button>
          </div>
          {upcoming.length === 0 && <div className="empty small">Yaklaşan duruşma yok.</div>}
          {upcoming.map((c) => {
            const [y, m, d] = (c.next_date || "----").split("-");
            const months = ["OCA", "ŞUB", "MAR", "NİS", "MAY", "HAZ", "TEM", "AĞU", "EYL", "EKİ", "KAS", "ARA"];
            return (
              <div className="deadline" key={c.id}>
                <div className="date-box">
                  <b>{d || "--"}</b>
                  <span>{months[parseInt(m || "1") - 1] || m}</span>
                </div>
                <div>
                  <strong>Duruşma — {c.title}</strong>
                  <span>{c.court} · {c.hearing_time || "—"}</span>
                </div>
                <MoreHorizontal size={16} />
              </div>
            );
          })}
        </section>
      </div>

      <div className="bottom-grid">
        <section className="panel">
          <PanelHead title="Son cari hareketler" eyebrow="HAREKETLER" action="Tümünü gör" onClick={() => setActive("transactions")} />
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>AÇIKLAMA</th>
                  <th>MÜVEKKİL</th>
                  <th>TARİH</th>
                  <th className="align-right">TUTAR</th>
                </tr>
              </thead>
              <tbody>
                {(data.transactions || []).slice(0, 4).map((x) => (
                  <tr key={x.id}>
                    <td>
                      <div className="table-title">
                        <span className={`transaction-icon ${["Gider", "Ödeme"].includes(x.kind) ? "expense" : "income"}`}>
                          {["Gider", "Ödeme"].includes(x.kind) ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                        </span>
                        <strong>{x.description}</strong>
                      </div>
                    </td>
                    <td>{x.client_name}</td>
                    <td>{x.date}</td>
                    <td className={`align-right amount ${["Gider", "Ödeme"].includes(x.kind) ? "negative" : "positive"}`}>
                      {["Gider", "Ödeme"].includes(x.kind) ? "-" : "+"}{MONEY(x.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel quick">
          <span className="eyebrow">HIZLI İŞLEM</span>
          <h2>Bugün ne yapmak istersiniz?</h2>
          <button data-testid="quick-collection-button" onClick={() => openCreate("transactions")}>
            <span className="quick-icon gold-bg"><ArrowLeftRight size={18} /></span>Yeni tahsilat <span>→</span>
          </button>
          <button data-testid="quick-case-button" onClick={() => openCreate("cases")}>
            <span className="quick-icon blue-bg"><Scale size={18} /></span>Dosya ekle <span>→</span>
          </button>
          <button data-testid="quick-invoice-button" onClick={() => openCreate("invoices")}>
            <span className="quick-icon green-bg"><ReceiptText size={18} /></span>Fatura oluştur <span>→</span>
          </button>
          <button data-testid="quick-document-button" onClick={() => openCreate("documents")}>
            <span className="quick-icon orange-bg"><FileText size={18} /></span>Belge yükle <span>→</span>
          </button>
        </section>
      </div>
    </>
  );
}

function Metric({ label, value, suffix, icon: Icon, tone, testId }) {
  return (
    <div className="metric" data-testid={testId}>
      <div className={`metric-icon ${tone}`}><Icon size={18} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}<small>{suffix}</small></strong>
      </div>
      <MoreHorizontal size={16} />
    </div>
  );
}

function PanelHead({ title, eyebrow, action, onClick }) {
  return (
    <div className="panel-head">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
      </div>
      {action && (
        <button data-testid={`view-${title.replaceAll(" ", "-").toLowerCase()}`} className="text-btn" onClick={onClick}>
          {action} <span>→</span>
        </button>
      )}
    </div>
  );
}

/* ---------------- MODULE (list views for clients/transactions/invoices/cases) ---------------- */
function Module({ id, list, query, setQuery, onEdit, onBulkDelete }) {
  const [selected, setSelected] = useState(new Set());
  const visibleIds = list.map((x) => x.id).filter(Boolean);
  const allSelected = visibleIds.length > 0 && visibleIds.every((i) => selected.has(i));
  const toggleOne = (rowId) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      if (visibleIds.every((i) => s.has(i))) return new Set();
      return new Set(visibleIds);
    });
  };
  const handleBulk = () => {
    onBulkDelete(Array.from(selected));
    setSelected(new Set());
  };
  const exportExcel = () => {
    window.open(`${API}/${id}/export`, "_blank");
  };

  return (
    <section className="panel module-panel">
      <div className="module-toolbar">
        <div className="search">
          <Search size={17} />
          <input
            data-testid={`${id}-search-input`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Kayıtlarda ara..."
          />
        </div>
        <div className="filter-row">
          {selected.size > 0 ? (
            <button data-testid={`${id}-bulk-delete-btn`} className="danger-btn compact" onClick={handleBulk}>
              <Trash2 size={14} /> Seçilenleri sil ({selected.size})
            </button>
          ) : (
            <div className="hint-pill"><span>Kayda tıklayarak düzenleyin · Kutucuklarla toplu silin</span></div>
          )}
          {list.length > 0 && (
            <button data-testid={`${id}-export-btn`} className="secondary-btn compact" onClick={exportExcel} title="Excel'e aktar">
              <FileDown size={14} /> Excel'e aktar
            </button>
          )}
        </div>
      </div>

      {list.length === 0 && <div className="empty">Kayıt bulunamadı.</div>}

      {/* Desktop table */}
      {list.length > 0 && (
        <div className="table-wrap desktop-only">
          <table>
            <thead>
              <tr>
                <th className="check-col">
                  <input
                    type="checkbox"
                    data-testid={`${id}-select-all`}
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                </th>
                {tableHeaderCells(id)}
              </tr>
            </thead>
            <tbody>{list.map((x, i) => tableRow(id, x, i, onEdit, selected, toggleOne))}</tbody>
          </table>
        </div>
      )}

      {/* Mobile cards */}
      <div className="mobile-cards">
        {list.map((x, i) => mobileCard(id, x, i, onEdit, selected, toggleOne))}
      </div>
    </section>
  );
}

function tableHeaderCells(id) {
  const map = {
    clients: ["MÜVEKKİL", "T.C. / VKN", "İLETİŞİM", "BAKİYE", "DURUM"],
    transactions: ["AÇIKLAMA", "MÜVEKKİL", "ÖDEME", "TARİH", "TUTAR"],
    invoices: ["FATURA NO", "MÜVEKKİL", "TÜR", "TARİH", "TOPLAM"],
    cases: ["DOSYA NO", "KONU", "MAHKEME", "DURUŞMA", "MÜVEKKİL"],
  };
  return (map[id] || []).map((h) => <th key={h} className={h === "TUTAR" || h === "TOPLAM" || h === "BAKİYE" ? "align-right" : ""}>{h}</th>).concat(<th key="status" className="align-right">DURUM</th>);
}

const ROW_RENDERERS = {
  clients: ClientRow,
  transactions: TransactionRow,
  invoices: InvoiceRow,
  cases: CaseRow,
};

function tableRow(id, x, i, onEdit, selected, toggleOne) {
  const Renderer = ROW_RENDERERS[id];
  return Renderer ? <Renderer key={x.id || i} row={x} index={i} onEdit={onEdit} selected={selected} toggleOne={toggleOne} /> : null;
}

function SelectCell({ id, checked, toggle, testIdPrefix }) {
  return (
    <td className="check-col" onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        data-testid={`${testIdPrefix}-select-${id}`}
        checked={checked}
        onChange={() => toggle(id)}
      />
    </td>
  );
}

function ClientRow({ row: x, index: i, onEdit, selected, toggleOne }) {
  return (
    <tr data-testid={`row-clients-${i}`} className="clickable-row" onClick={() => onEdit && onEdit(x)}>
      <SelectCell id={x.id} checked={selected.has(x.id)} toggle={toggleOne} testIdPrefix="clients" />
      <td><div className="table-title"><div className="row-avatar">{(x.name || "K").slice(0, 2).toUpperCase()}</div><div><strong>{x.name}</strong><span className="muted-sub">{x.type}</span></div></div></td>
      <td>{x.identity_no || "—"}</td>
      <td><div className="stack-sm"><span>{x.phone || "—"}</span><span className="muted-sub">{x.email || ""}</span></div></td>
      <td className={`align-right amount ${x.balance < 0 ? "negative" : "positive"}`}>{MONEY(x.balance)}</td>
      <td className="align-right"><span className="status success">{x.status || "Aktif"}</span></td>
    </tr>
  );
}

function TransactionRow({ row: x, index: i, onEdit, selected, toggleOne }) {
  const isNeg = ["Gider", "Ödeme"].includes(x.kind);
  const Icon = PAYMENT_ICONS[x.payment_method] || Wallet;
  return (
    <tr data-testid={`row-transactions-${i}`} className="clickable-row" onClick={() => onEdit && onEdit(x)}>
      <SelectCell id={x.id} checked={selected.has(x.id)} toggle={toggleOne} testIdPrefix="transactions" />
      <td><div className="table-title"><span className={`transaction-icon ${isNeg ? "expense" : "income"}`}>{isNeg ? <TrendingDown size={14} /> : <TrendingUp size={14} />}</span><div><strong>{x.description}</strong><span className="muted-sub">{x.category || "Genel"}</span></div></div></td>
      <td>{x.client_name}</td>
      <td><span className="pill"><Icon size={12} /> {x.payment_method || "Nakit"}</span></td>
      <td>{x.date}</td>
      <td className={`align-right amount ${isNeg ? "negative" : "positive"}`}>{isNeg ? "-" : "+"}{MONEY(x.amount)}</td>
    </tr>
  );
}

function InvoiceRow({ row: x, index: i, onEdit, selected, toggleOne }) {
  return (
    <tr data-testid={`row-invoices-${i}`} className="clickable-row" onClick={() => onEdit && onEdit(x)}>
      <SelectCell id={x.id} checked={selected.has(x.id)} toggle={toggleOne} testIdPrefix="invoices" />
      <td><div className="table-title"><span className="row-avatar gold-bg">{x.kind === "Alış" ? "A" : "S"}</span><strong>{x.number}</strong></div></td>
      <td>{x.client}</td>
      <td><span className={`pill ${x.kind === "Alış" ? "pill-blue" : "pill-gold"}`}>{x.kind}</span></td>
      <td>{x.date}</td>
      <td className="align-right amount positive">{MONEY(x.total)}</td>
      <td className="align-right"><span className={`status ${x.status === "Bekliyor" ? "warning" : "success"}`}>{x.status}</span></td>
    </tr>
  );
}

function CaseRow({ row: x, index: i, onEdit, selected, toggleOne }) {
  return (
    <tr data-testid={`row-cases-${i}`} className="clickable-row" onClick={() => onEdit && onEdit(x)}>
      <SelectCell id={x.id} checked={selected.has(x.id)} toggle={toggleOne} testIdPrefix="cases" />
      <td><div className="table-title"><span className={`row-avatar ${x.type === "İcra" ? "blue-bg" : "gold-bg"}`}>{x.type === "İcra" ? "İ" : "D"}</span><strong>{x.number}</strong></div></td>
      <td>{x.title}</td>
      <td>{x.court}</td>
      <td><div className="stack-sm"><span>{x.next_date}</span><span className="muted-sub"><Clock size={10} style={{ display: "inline", marginRight: 3 }} />{x.hearing_time || "—"}</span></div></td>
      <td>{x.client}</td>
      <td className="align-right"><span className={`status ${x.status === "Yeni" ? "info" : "success"}`}>{x.status}</span></td>
    </tr>
  );
}

const CARD_RENDERERS = {
  clients: ClientCard,
  transactions: TransactionCard,
  invoices: InvoiceCard,
  cases: CaseCard,
};

function mobileCard(id, x, i, onEdit, selected, toggleOne) {
  const Renderer = CARD_RENDERERS[id];
  return Renderer ? <Renderer key={x.id || i} row={x} index={i} onEdit={onEdit} selected={selected} toggleOne={toggleOne} /> : null;
}

function MobileCheckbox({ id, checked, toggle, type }) {
  return (
    <input
      type="checkbox"
      className="mcard-check"
      data-testid={`${type}-mselect-${id}`}
      checked={checked}
      onChange={() => toggle(id)}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function ClientCard({ row: x, index: i, onEdit, selected, toggleOne }) {
  return (
    <div className="mcard clickable-row" data-testid={`mrow-clients-${i}`} onClick={() => onEdit && onEdit(x)}>
      <div className="mcard-head">
        <MobileCheckbox id={x.id} checked={selected.has(x.id)} toggle={toggleOne} type="clients" />
        <div className="row-avatar">{(x.name || "K").slice(0, 2).toUpperCase()}</div>
        <div className="mcard-title"><strong>{x.name}</strong><span>{x.type} · {x.identity_no || "—"}</span></div>
        <span className={`amount ${x.balance < 0 ? "negative" : "positive"}`}>{MONEY(x.balance)}</span>
      </div>
      <div className="mcard-meta"><span>{x.phone || "—"}</span><span>{x.email || "—"}</span></div>
    </div>
  );
}

function TransactionCard({ row: x, index: i, onEdit, selected, toggleOne }) {
  const isNeg = ["Gider", "Ödeme"].includes(x.kind);
  return (
    <div className="mcard clickable-row" data-testid={`mrow-transactions-${i}`} onClick={() => onEdit && onEdit(x)}>
      <div className="mcard-head">
        <MobileCheckbox id={x.id} checked={selected.has(x.id)} toggle={toggleOne} type="transactions" />
        <span className={`transaction-icon ${isNeg ? "expense" : "income"}`}>{isNeg ? <TrendingDown size={14} /> : <TrendingUp size={14} />}</span>
        <div className="mcard-title"><strong>{x.description}</strong><span>{x.client_name} · {x.date}</span></div>
        <span className={`amount ${isNeg ? "negative" : "positive"}`}>{isNeg ? "-" : "+"}{MONEY(x.amount)}</span>
      </div>
      <div className="mcard-meta"><span className="pill">{x.payment_method}</span><span className="pill">{x.category}</span></div>
    </div>
  );
}

function InvoiceCard({ row: x, index: i, onEdit, selected, toggleOne }) {
  return (
    <div className="mcard clickable-row" data-testid={`mrow-invoices-${i}`} onClick={() => onEdit && onEdit(x)}>
      <div className="mcard-head">
        <MobileCheckbox id={x.id} checked={selected.has(x.id)} toggle={toggleOne} type="invoices" />
        <span className="row-avatar gold-bg">{x.kind === "Alış" ? "A" : "S"}</span>
        <div className="mcard-title"><strong>{x.number}</strong><span>{x.client} · {x.date}</span></div>
        <span className="amount positive">{MONEY(x.total)}</span>
      </div>
      <div className="mcard-meta"><span className={`pill ${x.kind === "Alış" ? "pill-blue" : "pill-gold"}`}>{x.kind}</span><span className={`status ${x.status === "Bekliyor" ? "warning" : "success"}`}>{x.status}</span></div>
    </div>
  );
}

function CaseCard({ row: x, index: i, onEdit, selected, toggleOne }) {
  return (
    <div className="mcard clickable-row" data-testid={`mrow-cases-${i}`} onClick={() => onEdit && onEdit(x)}>
      <div className="mcard-head">
        <MobileCheckbox id={x.id} checked={selected.has(x.id)} toggle={toggleOne} type="cases" />
        <span className={`row-avatar ${x.type === "İcra" ? "blue-bg" : "gold-bg"}`}>{x.type === "İcra" ? "İ" : "D"}</span>
        <div className="mcard-title"><strong>{x.number} · {x.title}</strong><span>{x.court}</span></div>
      </div>
      <div className="mcard-meta"><span><MapPin size={11} style={{ marginRight: 4 }} />{x.client}</span><span><Clock size={11} style={{ marginRight: 4 }} />{x.next_date} {x.hearing_time && `· ${x.hearing_time}`}</span></div>
    </div>
  );
}

/* ---------------- REPORTS ---------------- */
function Reports({ reports }) {
  const { monthly = [], categories = [], total_income = 0, total_expense = 0, net = 0 } = reports;
  const monthNames = { "01": "Oca", "02": "Şub", "03": "Mar", "04": "Nis", "05": "May", "06": "Haz", "07": "Tem", "08": "Ağu", "09": "Eyl", "10": "Eki", "11": "Kas", "12": "Ara" };
  const maxVal = Math.max(1, ...monthly.map((m) => Math.max(m.income, m.expense)));

  return (
    <>
      <div className="metric-grid">
        <Metric label="Toplam Gelir" value={MONEY(total_income)} suffix="" icon={TrendingUp} tone="green" testId="reports-income" />
        <Metric label="Toplam Gider" value={MONEY(total_expense)} suffix="" icon={TrendingDown} tone="orange" testId="reports-expense" />
        <Metric label="Net Sonuç" value={MONEY(net)} suffix="" icon={Wallet} tone={net >= 0 ? "gold" : "orange"} testId="reports-net" />
        <Metric label="Kategori Sayısı" value={categories.length} suffix=" kalem" icon={BarChart3} tone="blue" testId="reports-categories" />
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <PanelHead title="Aylık gelir & gider" eyebrow="ZAMAN SERİSİ" />
          {monthly.length === 0 && <div className="empty">Henüz hareket kaydı yok.</div>}
          <div className="bar-chart" data-testid="reports-bar-chart">
            {monthly.map((m) => (
              <div className="bar-col" key={m.month}>
                <div className="bar-stack">
                  <div className="bar income" style={{ height: `${(m.income / maxVal) * 100}%` }} title={`Gelir: ${MONEY(m.income)}`}></div>
                  <div className="bar expense" style={{ height: `${(m.expense / maxVal) * 100}%` }} title={`Gider: ${MONEY(m.expense)}`}></div>
                </div>
                <span className="bar-label">{monthNames[m.month.slice(5, 7)]} {m.month.slice(2, 4)}</span>
              </div>
            ))}
          </div>
          <div className="chart-legend">
            <span><i className="dot income"></i>Gelir</span>
            <span><i className="dot expense"></i>Gider</span>
          </div>
        </section>

        <section className="panel">
          <PanelHead title="Kategori dağılımı" eyebrow="KIRILIM" />
          {categories.length === 0 && <div className="empty">Kategori verisi yok.</div>}
          <div className="cat-list">
            {categories.map((c) => (
              <div className="cat-row" key={c.name} data-testid={`cat-${c.name}`}>
                <div className="cat-name">{c.name}</div>
                <div className="cat-bars">
                  <div className="cat-bar income" style={{ width: `${(c.income / Math.max(1, total_income)) * 100}%` }}></div>
                  <div className="cat-bar expense" style={{ width: `${(c.expense / Math.max(1, total_expense)) * 100}%` }}></div>
                </div>
                <div className="cat-val">
                  <span className="positive">+{MONEY(c.income)}</span>
                  <span className="negative">-{MONEY(c.expense)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

/* ---------------- CALENDAR ---------------- */
function Calendar({ events, cases, onEditEvent, onEditCase }) {
  const grouped = useMemo(() => {
    const g = {};
    events.forEach((e) => {
      const key = e.date || "Belirsiz";
      g[key] = g[key] || [];
      g[key].push(e);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  const handleClick = (item) => {
    // Auto-derived from cases: id starts with "case-"; open the case for editing
    if (typeof item.id === "string" && item.id.startsWith("case-")) {
      const relatedCase = cases.find((c) => c.number === item.related_case);
      if (relatedCase) onEditCase(relatedCase);
      else toast.error("Bağlı dosya bulunamadı");
      return;
    }
    onEditEvent(item);
  };

  if (events.length === 0) {
    return <section className="panel"><div className="empty">Kayıtlı etkinlik yok. Duruşma eklediğinizde burada listelenecek.</div></section>;
  }

  return (
    <section className="panel">
      <PanelHead title="Takvim & etkinlikler" eyebrow="ZAMAN ÇİZELGESİ" />
      <div className="hint-pill" style={{ marginBottom: 16 }}>
        <span>Etkinliğe tıklayarak düzenleyin. Duruşmalar bağlı dosyayı açar.</span>
      </div>
      <div className="timeline" data-testid="calendar-timeline">
        {grouped.map(([date, items]) => (
          <div className="tl-group" key={date}>
            <div className="tl-date">{date}</div>
            <div className="tl-items">
              {items.map((it) => (
                <div
                  className="tl-item clickable-row"
                  key={it.id}
                  data-testid={`cal-${it.id}`}
                  onClick={() => handleClick(it)}
                >
                  <div className={`tl-dot ${it.kind === "Duruşma" ? "gold" : it.kind === "Ödeme" ? "green" : "blue"}`}></div>
                  <div className="tl-body">
                    <strong>{it.title}</strong>
                    <span>{it.description} {it.time && `· ${it.time}`}</span>
                  </div>
                  <span className={`pill ${it.kind === "Duruşma" ? "pill-gold" : "pill-blue"}`}>{it.kind}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- DOCUMENTS ---------------- */
function Documents({ docs, query, setQuery, onEdit, onBulkDelete }) {
  const [selected, setSelected] = useState(new Set());
  const visibleIds = docs.map((d) => d.id).filter(Boolean);
  const allSelected = visibleIds.length > 0 && visibleIds.every((i) => selected.has(i));
  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected((s) => (visibleIds.every((i) => s.has(i)) ? new Set() : new Set(visibleIds)));
  const handleBulk = () => { onBulkDelete(Array.from(selected)); setSelected(new Set()); };

  return (
    <section className="panel module-panel">
      <div className="module-toolbar">
        <div className="search">
          <Search size={17} />
          <input data-testid="documents-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Belgelerde ara..." />
        </div>
        <div className="filter-row">
          {selected.size > 0 ? (
            <button data-testid="documents-bulk-delete-btn" className="danger-btn compact" onClick={handleBulk}>
              <Trash2 size={14} /> Seçilenleri sil ({selected.size})
            </button>
          ) : docs.length > 0 && (
            <label className="hint-pill" style={{ cursor: "pointer" }}>
              <input type="checkbox" data-testid="documents-select-all" checked={allSelected} onChange={toggleAll} style={{ marginRight: 8 }} />
              Tümünü seç
            </label>
          )}
          {docs.length > 0 && (
            <button data-testid="documents-export-btn" className="secondary-btn compact" onClick={() => window.open(`${API}/documents/export`, "_blank")}>
              <FileDown size={14} /> Excel'e aktar
            </button>
          )}
        </div>
      </div>

      {docs.length === 0 && <div className="empty">Belge bulunamadı. "Yeni Kayıt" ile ekleyin.</div>}

      <div className="doc-grid" data-testid="documents-grid">
        {docs.map((d) => (
          <div className={`doc-card clickable-row ${selected.has(d.id) ? "selected" : ""}`} key={d.id} data-testid={`doc-${d.id}`} onClick={() => onEdit && onEdit(d)}>
            <input
              type="checkbox"
              className="doc-check"
              data-testid={`documents-select-${d.id}`}
              checked={selected.has(d.id)}
              onChange={() => toggleOne(d.id)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="doc-icon"><FileCheck2 size={22} /></div>
            <div className="doc-body">
              <strong>{d.name}</strong>
              <span className="muted-sub">{d.client || "—"} · Dosya: {d.case_number || "—"}</span>
              <div className="doc-meta">
                <span className="pill">{d.file_type}</span>
                <span className="muted-sub">{d.size_kb} KB · {d.date}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- MODAL ---------------- */
function Modal({ type, mode = "create", record = null, data, onClose, onSave, onUpdate, onDelete }) {
  const defaults = {
    clients: { type: "Gerçek kişi", status: "Aktif" },
    transactions: { kind: "Tahsilat", payment_method: "Nakit", category: "Vekalet", date: TODAY },
    invoices: { kind: "Satış", vat: 20, withholding: 0, withholding_type: "Yok", status: "Bekliyor", date: TODAY },
    cases: { type: "Dava", status: "Yeni" },
    documents: { file_type: "PDF", date: TODAY },
    calendar: { kind: "Duruşma", date: TODAY },
  }[type];

  const initial = mode === "edit" && record ? { ...defaults, ...record } : defaults;
  const [form, setForm] = useState(initial);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const clientNames = CLIENT_OPTIONS(data);
  const titles = {
    clients: mode === "edit" ? "Müvekkil düzenle" : "Yeni müvekkil",
    transactions: mode === "edit" ? "Cari hareket düzenle" : "Yeni cari hareket",
    invoices: mode === "edit" ? "Fatura düzenle" : "Yeni fatura",
    cases: mode === "edit" ? "Dosya düzenle" : "Yeni dosya",
    documents: mode === "edit" ? "Belge düzenle" : "Yeni belge",
    calendar: mode === "edit" ? "Etkinlik düzenle" : "Yeni takvim etkinliği",
  };

  const submit = () => {
    let payload = { ...form };
    ["amount", "total", "balance", "vat", "withholding", "size_kb"].forEach((k) => {
      if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
    });
    if (!payload.date) payload.date = TODAY;
    if (mode === "edit" && record) {
      onUpdate(type, record.id, payload);
    } else {
      onSave(payload, type);
    }
  };

  const handleDelete = () => {
    if (mode === "edit" && record) onDelete(type, record.id);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{mode === "edit" ? "KAYDI DÜZENLE" : "KAYIT OLUŞTUR"}</span>
            <h2>{titles[type]}</h2>
          </div>
          <button data-testid="modal-close-button" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          <ModalBody type={type} form={form} upd={upd} clientNames={clientNames} />
        </div>

        <div className="modal-actions">
          {mode === "edit" && type === "invoices" && record?.id && (
            <a
              data-testid="invoice-pdf-btn"
              className="secondary-btn compact"
              href={`${API}/invoices/${record.id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download size={14} /> PDF indir
            </a>
          )}
          {mode === "edit" && (
            <button data-testid="modal-delete-button" className="danger-btn" onClick={handleDelete}>
              Sil
            </button>
          )}
          <button data-testid="modal-cancel-button" className="secondary-btn" onClick={onClose}>Vazgeç</button>
          <button data-testid="modal-save-button" className="primary-btn" onClick={submit}>
            {mode === "edit" ? "Güncelle" : "Kaydet"} <CheckCircle2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

const MODAL_BODIES = {
  clients: ClientForm,
  transactions: TransactionForm,
  invoices: InvoiceForm,
  cases: CaseForm,
  documents: DocumentForm,
  calendar: CalendarForm,
};

function ModalBody({ type, form, upd, clientNames }) {
  const Body = MODAL_BODIES[type];
  return Body ? <Body form={form} upd={upd} clientNames={clientNames} /> : null;
}

function ClientForm({ form, upd }) {
  return (
    <>
      <Field label="Müvekkil adı" testId="modal-name-input" value={form.name} onChange={(v) => upd("name", v)} />
      <div className="inline-fields">
        <Field label="Tür">
          <select data-testid="modal-type-select" value={form.type} onChange={(e) => upd("type", e.target.value)}>
            <option>Gerçek kişi</option>
            <option>Tüzel kişi</option>
          </select>
        </Field>
        <Field label="T.C. / Vergi kimlik no" testId="modal-identity_no-input" value={form.identity_no} onChange={(v) => upd("identity_no", v)} />
      </div>
      <div className="inline-fields">
        <Field label="Telefon" testId="modal-phone-input" value={form.phone} onChange={(v) => upd("phone", v)} />
        <Field label="E-posta" testId="modal-email-input" value={form.email} onChange={(v) => upd("email", v)} />
      </div>
      <Field label="Başlangıç bakiyesi (₺)" testId="modal-balance-input" type="number" value={form.balance} onChange={(v) => upd("balance", v)} />
    </>
  );
}

function TransactionForm({ form, upd, clientNames }) {
  return (
    <>
      <Field label="Müvekkil / cari">
        <select data-testid="modal-client_name-input" value={form.client_name || ""} onChange={(e) => upd("client_name", e.target.value)}>
          <option value="">Müvekkil seçin</option>
          {clientNames.map((c) => <option key={c}>{c}</option>)}
          <option>Büro Genel</option>
        </select>
      </Field>
      <Field label="Açıklama" testId="modal-description-input" value={form.description} onChange={(v) => upd("description", v)} />
      <div className="inline-fields">
        <Field label="Hareket türü">
          <select data-testid="modal-kind-select" value={form.kind} onChange={(e) => upd("kind", e.target.value)}>
            <option>Tahsilat</option>
            <option>Ödeme</option>
            <option>Gelir</option>
            <option>Gider</option>
          </select>
        </Field>
        <Field label="Ödeme şekli">
          <select data-testid="modal-payment_method-select" value={form.payment_method} onChange={(e) => upd("payment_method", e.target.value)}>
            <option>Nakit</option>
            <option>Kredi kartı</option>
            <option>Havale / EFT</option>
          </select>
        </Field>
      </div>
      <div className="inline-fields">
        <Field label="Kategori">
          <select data-testid="modal-category-select" value={form.category} onChange={(e) => upd("category", e.target.value)}>
            <option>Vekalet</option>
            <option>Danışmanlık</option>
            <option>Harç ve masraf</option>
            <option>Ofis</option>
            <option>Personel</option>
            <option>Diğer</option>
          </select>
        </Field>
        <Field label="Tutar (₺)" testId="modal-amount-input" type="number" value={form.amount} onChange={(v) => upd("amount", v)} />
      </div>
      <Field label="Tarih" testId="modal-date-input" type="date" value={form.date} onChange={(v) => upd("date", v)} />
    </>
  );
}

function InvoiceForm({ form, upd, clientNames }) {
  return (
    <>
      <div className="inline-fields">
        <Field label="Fatura tipi">
          <select data-testid="modal-kind-select" value={form.kind} onChange={(e) => upd("kind", e.target.value)}>
            <option>Satış</option>
            <option>Alış</option>
          </select>
        </Field>
        <Field label="Fatura numarası" testId="modal-number-input" value={form.number} onChange={(v) => upd("number", v)} />
      </div>
      <Field label="Müvekkil / karşı taraf">
        <select data-testid="modal-client-input" value={form.client || ""} onChange={(e) => upd("client", e.target.value)}>
          <option value="">Seçin</option>
          {clientNames.map((c) => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <div className="inline-fields">
        <Field label="Toplam tutar (₺)" testId="modal-total-input" type="number" value={form.total} onChange={(v) => upd("total", v)} />
        <Field label="Tarih" testId="modal-date-input" type="date" value={form.date} onChange={(v) => upd("date", v)} />
      </div>
      <div className="inline-fields">
        <Field label="KDV %" testId="modal-vat-input" type="number" value={form.vat} onChange={(v) => upd("vat", v)} />
        <Field label="Stopaj %" testId="modal-withholding-input" type="number" value={form.withholding} onChange={(v) => upd("withholding", v)} />
      </div>
      <Field label="Tevkifat">
        <select data-testid="modal-withholding_type-select" value={form.withholding_type} onChange={(e) => upd("withholding_type", e.target.value)}>
          <option>Yok</option>
          <option>Serbest meslek (5/10)</option>
          <option>Kira (2/10)</option>
          <option>Diğer</option>
        </select>
      </Field>
      <Field label="Durum">
        <select data-testid="modal-status-select" value={form.status} onChange={(e) => upd("status", e.target.value)}>
          <option>Bekliyor</option>
          <option>Ödendi</option>
        </select>
      </Field>
    </>
  );
}

function CaseForm({ form, upd, clientNames }) {
  return (
    <>
      <div className="inline-fields">
        <Field label="Dosya numarası" testId="modal-number-input" value={form.number} onChange={(v) => upd("number", v)} />
        <Field label="Tür">
          <select data-testid="modal-type-select" value={form.type} onChange={(e) => upd("type", e.target.value)}>
            <option>Dava</option>
            <option>İcra</option>
          </select>
        </Field>
      </div>
      <Field label="Dosya konusu" testId="modal-title-input" value={form.title} onChange={(v) => upd("title", v)} />
      <Field label="Mahkeme / Daire" testId="modal-court-input" value={form.court} onChange={(v) => upd("court", v)} />
      <Field label="Müvekkil">
        <select data-testid="modal-client-input" value={form.client || ""} onChange={(e) => upd("client", e.target.value)}>
          <option value="">Seçin</option>
          {clientNames.map((c) => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <div className="inline-fields">
        <Field label="Duruşma tarihi" testId="modal-next_date-input" type="date" value={form.next_date} onChange={(v) => upd("next_date", v)} />
        <Field label="Duruşma saati" testId="modal-hearing_time-input" type="time" value={form.hearing_time} onChange={(v) => upd("hearing_time", v)} />
      </div>
      <Field label="Sorumlu avukat" testId="modal-lawyer-input" value={form.lawyer || "Av. Görkem Peker"} onChange={(v) => upd("lawyer", v)} />
      <Field label="Konu detayı" testId="modal-subject-input" value={form.subject} onChange={(v) => upd("subject", v)} />
    </>
  );
}

function DocumentForm({ form, upd, clientNames }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("name", form.name || r.data.filename);
      upd("file_url", r.data.url);
      upd("file_type", (r.data.filename.split(".").pop() || "").toUpperCase() || "PDF");
      upd("size_kb", Math.round(r.data.size / 1024));
      toast.success("Dosya yüklendi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yüklenemedi");
    } finally {
      setUploading(false);
    }
  };
  return (
    <>
      <Field label="Belge adı" testId="modal-name-input" value={form.name} onChange={(v) => upd("name", v)} />
      <label className="upload-zone" data-testid="doc-upload-zone">
        <input type="file" data-testid="doc-file-input" onChange={handleFile} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" style={{ display: "none" }} />
        <Upload size={18} />
        <span>{uploading ? "Yükleniyor..." : "Dosya seçmek için tıklayın (PDF, DOCX, JPG, PNG • max 15MB)"}</span>
      </label>
      {form.file_url && (
        <a href={`${process.env.REACT_APP_BACKEND_URL}${form.file_url}`} target="_blank" rel="noopener noreferrer" className="uploaded-file" data-testid="doc-preview-link">
          <Paperclip size={13} /> Yüklenen dosyayı indir <ExternalLink size={11} />
        </a>
      )}
      <div className="inline-fields">
        <Field label="Dosya no" testId="modal-case_number-input" value={form.case_number} onChange={(v) => upd("case_number", v)} />
        <Field label="Müvekkil">
          <select data-testid="modal-client-input" value={form.client || ""} onChange={(e) => upd("client", e.target.value)}>
            <option value="">Seçin</option>
            {clientNames.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <div className="inline-fields">
        <Field label="Tür">
          <select data-testid="modal-file_type-select" value={form.file_type} onChange={(e) => upd("file_type", e.target.value)}>
            <option>PDF</option>
            <option>DOCX</option>
            <option>Görsel</option>
            <option>Diğer</option>
          </select>
        </Field>
        <Field label="Boyut (KB)" testId="modal-size_kb-input" type="number" value={form.size_kb} onChange={(v) => upd("size_kb", v)} />
      </div>
    </>
  );
}

function CalendarForm({ form, upd }) {
  return (
    <>
      <Field label="Başlık" testId="modal-title-input" value={form.title} onChange={(v) => upd("title", v)} />
      <Field label="Açıklama" testId="modal-description-input" value={form.description} onChange={(v) => upd("description", v)} />
      <div className="inline-fields">
        <Field label="Tarih" testId="modal-date-input" type="date" value={form.date} onChange={(v) => upd("date", v)} />
        <Field label="Saat" testId="modal-time-input" type="time" value={form.time} onChange={(v) => upd("time", v)} />
      </div>
      <Field label="Tür">
        <select data-testid="modal-kind-select" value={form.kind} onChange={(e) => upd("kind", e.target.value)}>
          <option>Duruşma</option>
          <option>Görev</option>
          <option>Ödeme</option>
          <option>Hatırlatma</option>
        </select>
      </Field>
    </>
  );
}

function Field({ label, value, onChange, type = "text", testId, children }) {
  return (
    <label>
      {label}
      {children ? children : (
        <input
          data-testid={testId}
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={label}
        />
      )}
    </label>
  );
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const ROLES = ["Yönetici", "Büro çalışanı", "Avukat"];
const errMsg = (e, fb) => formatApiError(e.response?.data?.detail) || fb;

function NewUserForm({ onCreated }) {
  const blank = { name: "", email: "", password: "", role: "Büro çalışanı" };
  const [form, setForm] = useState(blank);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/users`, form);
      onCreated(r.data);
      setForm(blank);
      toast.success("Kullanıcı oluşturuldu");
    } catch (err) {
      toast.error(errMsg(err, "Kullanıcı oluşturulamadı"));
    }
  };
  return (
    <form className="new-user-form" onSubmit={submit} data-testid="new-user-form">
      <div className="inline-fields">
        <Field label="Ad soyad" testId="new-user-name" value={form.name} onChange={(v) => upd("name", v)} />
        <Field label="E-posta" type="email" testId="new-user-email" value={form.email} onChange={(v) => upd("email", v)} />
      </div>
      <div className="inline-fields">
        <Field label="Geçici şifre (min. 8)" type="password" testId="new-user-password" value={form.password} onChange={(v) => upd("password", v)} />
        <Field label="Rol">
          <select data-testid="new-user-role" value={form.role} onChange={(e) => upd("role", e.target.value)}>
            {ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
        </Field>
      </div>
      <button data-testid="new-user-submit-btn" type="submit" className="primary-btn"><Plus size={15} /> Kullanıcı ekle</button>
    </form>
  );
}

function UserActions({ u, isMe, onDeleted }) {
  const [resetting, setResetting] = useState(false);
  const [pwd, setPwd] = useState("");
  const reset = async () => {
    try {
      await axios.put(`${API}/users/${u.user_id}/password`, { password: pwd });
      toast.success("Şifre sıfırlandı");
      setResetting(false);
      setPwd("");
    } catch (e) {
      toast.error(errMsg(e, "Şifre sıfırlanamadı"));
    }
  };
  const remove = async () => {
    if (!window.confirm(`${u.name} kullanıcısı silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/users/${u.user_id}`);
      onDeleted(u.user_id);
      toast.success("Kullanıcı silindi");
    } catch (e) {
      toast.error(errMsg(e, "Kullanıcı silinemedi"));
    }
  };
  if (resetting) {
    return (
      <div className="user-reset">
        <input data-testid={`reset-password-input-${u.user_id}`} type="password" placeholder="Yeni şifre" value={pwd} onChange={(e) => setPwd(e.target.value)} />
        <button data-testid={`reset-password-save-${u.user_id}`} className="primary-btn" onClick={reset}>Kaydet</button>
        <button className="icon-btn" onClick={() => setResetting(false)}><X size={14} /></button>
      </div>
    );
  }
  return (
    <div className="user-actions">
      <button data-testid={`reset-password-btn-${u.user_id}`} className="icon-btn" title="Şifre sıfırla" onClick={() => setResetting(true)}><KeyRound size={14} /></button>
      {!isMe && <button data-testid={`delete-user-btn-${u.user_id}`} className="icon-btn" title="Sil" onClick={remove}><Trash2 size={14} /></button>}
    </div>
  );
}

function UsersModal({ me, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users`);
      setUsers(r.data);
    } catch (e) {
      toast.error("Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeRole = async (userId, role) => {
    try {
      const r = await axios.put(`${API}/users/${userId}/role`, { role });
      setUsers((us) => us.map((u) => (u.user_id === userId ? r.data : u)));
      toast.success("Rol güncellendi");
    } catch (e) {
      toast.error(errMsg(e, "Rol güncellenemedi"));
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">EKİP YÖNETİMİ</span>
            <h2>Kullanıcılar & roller</h2>
          </div>
          <button data-testid="users-modal-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <NewUserForm onCreated={(u) => setUsers((us) => [...us, u])} />
        {loading ? <div className="empty">Yükleniyor...</div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>KULLANICI</th><th>E-POSTA</th><th>ROL</th><th className="align-right">İŞLEM</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <td>
                      <div className="table-title">
                        {u.picture ? <img className="avatar img" src={u.picture} alt={u.name} /> : <div className="row-avatar">{initials(u.name)}</div>}
                        <div><strong>{u.name}</strong>{u.user_id === me.user_id && <span className="muted-sub">Siz</span>}</div>
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <select
                        data-testid={`role-select-${u.user_id}`}
                        value={u.role}
                        disabled={u.user_id === me.user_id}
                        onChange={(e) => changeRole(u.user_id, e.target.value)}
                        style={{ maxWidth: 180 }}
                      >
                        {ROLES.map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="align-right">
                      <UserActions u={u} isMe={u.user_id === me.user_id} onDeleted={(id) => setUsers((us) => us.filter((x) => x.user_id !== id))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="modal-actions">
          <button className="secondary-btn" onClick={onClose}>Kapat</button>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (form.new_password !== form.confirm) return toast.error("Yeni şifreler eşleşmiyor");
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: form.current_password, new_password: form.new_password });
      toast.success("Şifreniz güncellendi");
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Şifre değiştirilemedi"));
    }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="modal-head">
          <div>
            <span className="eyebrow">HESAP GÜVENLİĞİ</span>
            <h2>Şifremi değiştir</h2>
          </div>
          <button data-testid="change-password-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <Field label="Mevcut şifre" type="password" testId="current-password-input" value={form.current_password} onChange={(v) => upd("current_password", v)} />
          <Field label="Yeni şifre (min. 8 karakter)" type="password" testId="new-password-input" value={form.new_password} onChange={(v) => upd("new_password", v)} />
          <Field label="Yeni şifre (tekrar)" type="password" testId="confirm-password-input" value={form.confirm} onChange={(v) => upd("confirm", v)} />
        </div>
        <div className="modal-actions">
          <button className="secondary-btn" onClick={onClose}>Vazgeç</button>
          <button data-testid="change-password-save-btn" className="primary-btn" onClick={save}>Kaydet <CheckCircle2 size={16} /></button>
        </div>
      </div>
    </div>
  );
}

function FirmSettingsModal({ onClose }) {
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "", tax_office: "", tax_number: "", iban: "", logo_url: "" });
  const [loading, setLoading] = useState(true);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/settings/firm`).then((r) => setForm(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try {
      await axios.put(`${API}/settings/firm`, form);
      toast.success("Büro bilgileri güncellendi");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">FATURA BAŞLIĞI</span>
            <h2>Büro bilgileri</h2>
          </div>
          <button data-testid="firm-modal-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        {loading ? <div className="empty">Yükleniyor...</div> : (
          <div className="modal-body">
            <Field label="Büro adı" testId="firm-name" value={form.name} onChange={(v) => upd("name", v)} />
            <Field label="Adres" testId="firm-address" value={form.address} onChange={(v) => upd("address", v)} />
            <div className="inline-fields">
              <Field label="Telefon" testId="firm-phone" value={form.phone} onChange={(v) => upd("phone", v)} />
              <Field label="E-posta" testId="firm-email" value={form.email} onChange={(v) => upd("email", v)} />
            </div>
            <div className="inline-fields">
              <Field label="Vergi dairesi" testId="firm-tax-office" value={form.tax_office} onChange={(v) => upd("tax_office", v)} />
              <Field label="Vergi no" testId="firm-tax-number" value={form.tax_number} onChange={(v) => upd("tax_number", v)} />
            </div>
            <Field label="IBAN" testId="firm-iban" value={form.iban} onChange={(v) => upd("iban", v)} />
            <Field label="Logo URL (opsiyonel)" testId="firm-logo-url" value={form.logo_url} onChange={(v) => upd("logo_url", v)} />
            <div className="upload-hint"><Shield size={13} /> Bu bilgiler tüm fatura PDF'lerinizde başlık olarak görünür.</div>
          </div>
        )}
        <div className="modal-actions">
          <button data-testid="firm-cancel-btn" className="secondary-btn" onClick={onClose}>Vazgeç</button>
          <button data-testid="firm-save-btn" className="primary-btn" onClick={save}>Kaydet <CheckCircle2 size={16} /></button>
        </div>
      </div>
    </div>
  );
}

